/**
 * The exact commands a member runs on their own machine to connect Claude Code or Codex
 * to ONE agent. Every token is an opaque id or a lowercase slug, so the strings need no
 * quoting on any shell and never carry a key, a bearer token, or a local path.
 */

import { agentShortName } from "@baton/contracts";

export type AgentClient = "claude" | "codex";

export interface AgentCommandInput {
  apiUrl: string;
  agent: { id: string; handle: string; role?: string | null };
  client: AgentClient;
  projectName?: string | null;
  /** The signed-in member's handle, so the printed name drops the owner prefix. */
  ownerHandle?: string | null;
}

export interface AgentCommandStep {
  id: "node" | "install" | "login" | "open" | "project" | "register" | "verify" | "start" | "listen";
  title: string;
  command: string;
  note: string;
  /** login happens once per machine, register/verify once per agent, open/start/listen every session. */
  scope: "machine" | "agent" | "session";
  kind: "shell" | "prompt";
  /** Not needed for the normal path; shown collapsed. */
  optional?: boolean;
  /** Official installation page for a prerequisite that is not installed by npm. */
  link?: { label: string; href: string };
}

export function mcpServerName(handle: string): string {
  return `baton-${handle}`;
}

export function loginCommand(apiUrl: string): string {
  return `baton-mcp login --api-url ${apiUrl}`;
}

/**
 * One window, one agent: starts the client with only this agent's pinned bridge, a
 * listener that wakes it when a message arrives, and a status line naming the agent.
 * Handles are unique and readable, so no UUID appears in the command; a bare `baton`
 * shows the same choice as a list.
 */
export function openCommand(client: AgentClient, agent: { handle: string; role?: string | null }, ownerHandle?: string | null): string {
  return `baton open ${agentShortName(agent, ownerHandle)} ${client}`;
}

/** Manual alternative: one MCP server per agent; `--agent` pins the bridge so it binds this agent at startup. */
export function registerCommand(client: AgentClient, agent: { id: string; handle: string }): string {
  const server = mcpServerName(agent.handle);
  return client === "claude"
    ? `claude mcp add --transport stdio --scope user ${server} -- baton-mcp --agent ${agent.id}`
    : `codex mcp add ${server} -- baton-mcp --agent ${agent.id} --client codex`;
}

export function verifyCommand(client: AgentClient, agentId: string, mode: "full" | "quick" = "full"): string {
  return `baton-launch verify ${agentId} ${client}${mode === "quick" ? " --quick" : ""}`;
}

/** Removes the older machine-wide server so its tools are not duplicated next to the pinned one. */
export function cleanupCommand(client: AgentClient): string {
  return client === "claude" ? "claude mcp remove --scope user baton" : "codex mcp remove baton";
}

export function listenPrompt(agent: { id: string; handle: string }, projectName?: string | null): string {
  const where = projectName ? ` in project ${projectName}` : "";
  return `You are Baton agent ${agent.handle} (${agent.id})${where}. Call baton_whoami once to confirm you are bound to that agent, then baton_inbox once and print its drawing verbatim in a code block (each sender's box with an arrow into yours and the titles of their messages; never a prose summary). Then loop: call baton_await with timeoutSeconds 3600, passing the returned cursor each time; it returns as soon as a message arrives, and you print its drawing the same way. For every chat message, read it and answer with baton_reply (prepare, then commit after showing me the preview). For every handoff: baton_open, baton_ack, do the work in this repository, then baton_complete with a summary. Never push, merge, or deploy. Keep looping until I say stop; if a call errors, wait a few seconds and continue.`;
}

export function buildAgentCommands(input: AgentCommandInput): AgentCommandStep[] {
  const clientName = input.client === "claude" ? "Claude Code" : "Codex";
  return [
    {
      id: "node",
      title: "Install Node.js and npm",
      command: "node --version\nnpm --version",
      note: "Download Node.js LTS, version 22 or newer; npm is included. After installing, reopen PowerShell or Terminal and run these checks. Skip the download if both are already installed and Node is version 22 or newer.",
      link: { label: "Download Node.js LTS", href: "https://nodejs.org/en/download" },
      scope: "machine",
      kind: "shell",
    },
    {
      id: "install",
      title: "Install Baton, including the MCP bridge",
      command: "npm install -g batonx",
      note: "batonx is the npm installer, not another service. It bundles baton (opens the coding tool), baton-mcp (connects it to Baton), and baton-launch (an alias). No separate MCP package is needed. If your Baton MCP bridge already works, skip reinstalling. Browser-only sending needs none of these.",
      scope: "machine",
      kind: "shell",
    },
    {
      id: "login",
      title: "Sign this machine in",
      command: loginCommand(input.apiUrl),
      note: "Once per machine. Uses the GitHub CLI if present, otherwise a device code; credentials stay in a file only you can read.",
      scope: "machine",
      kind: "shell",
    },
    {
      id: "open",
      title: `Open a ${clientName} window for this agent`,
      command: openCommand(input.client, input.agent, input.ownerHandle),
      note: input.client === "claude"
        ? "Run it inside the repository you want the agent to work in, or run plain baton to pick from a list of your agents. One window is one agent: only this agent's bridge loads, a local listener wakes Claude the moment a message arrives (no polling), and the status line shows the agent and who is talking. Press Esc to interrupt listening and type."
        : "Run it inside the repository you want the agent to work in, or run plain baton to pick from a list of your agents. One window is one agent: only this agent's bridge loads. The bridge listens in the background; ask the agent to check its inbox when needed.",
      scope: "session",
      kind: "shell",
    },
    {
      id: "project",
      title: "Confirm the project for this checkout",
      command: `Set up Baton for this checkout. Call baton_route to find the project containing its GitHub repository${input.projectName ? `; the project selected in my dashboard is ${JSON.stringify(input.projectName)}` : ""}. Show the matching project and collaborators and ask me to confirm. If none matches, guide me through creating or joining a project and adding the repository. Do not send anything.`,
      note: "Paste this into the agent window. An admin adds repositories and invites people in Manage project; invitees must accept. MCP reads project IDs live from your signed-in Baton account, so there are no project IDs to paste into configuration. Afterwards, say 'send this to Nitant' or 'hand this over to Nitant'; the agent asks for the exact collaborator and project, then shows the draft for send approval.",
      scope: "session",
      kind: "prompt",
    },
    {
      id: "register",
      title: `Manual alternative: register the agent with plain ${clientName}`,
      command: registerCommand(input.client, input.agent),
      note: `If you prefer starting ${input.client} yourself: one MCP server named ${mcpServerName(input.agent.handle)} for this agent only; --agent binds it at startup. Every ${clientName} window then loads every registered agent.`,
      scope: "agent",
      kind: "shell",
      optional: true,
    },
    {
      id: "listen",
      title: "Manual alternative: paste the listen prompt",
      command: listenPrompt(input.agent, input.projectName),
      note: "Only for the manual path. The agent confirms its binding, waits for messages, answers chat with baton_reply, and handles handoffs open, ack, complete.",
      scope: "session",
      kind: "prompt",
      optional: true,
    },
    {
      id: "verify",
      title: "Optional: verify headless readiness",
      command: verifyCommand(input.client, input.agent.id),
      note: `Only for unattended baton_spawn launches later. Opening the window already records this machine as terminal-first. The full run drives ${clientName} through two real turns in a throwaway repo, so it takes minutes; ${verifyCommand(input.client, input.agent.id, "quick")} does the ten-second sanity check instead.`,
      scope: "agent",
      kind: "shell",
      optional: true,
    },
  ];
}
